<template>
  <div class="overflow-auto" @scroll="onContainerScroll">
    <table class="w-100">
      <thead>
        <tr v-if="boundedContext.systems?.length">
          <th v-if="showReadModels">
            <v-tooltip location="top" open-delay="500">
              <template v-slot:activator="{ props }">
                <span v-bind="props">{{ queryCardType?.title || 'Read Model' }}</span>
              </template>
              <span>Cards configured as queries and fields from the Read Model</span>
            </v-tooltip>
          </th>
          <th>
            <v-tooltip location="top" open-delay="500">
              <template v-slot:activator="{ props }">
                <span v-bind="props">Role</span>
              </template>
              <span>The role taken from the swim lanes</span>
            </v-tooltip>
          </th>
          <th>
            <v-tooltip location="top" open-delay="500">
              <template v-slot:activator="{ props }">
                <span v-bind="props">{{ commandCardType?.title || 'Command' }}</span>
              </template>
              <span>Cards configured as commands and command input fields</span>
            </v-tooltip>
          </th>
          <th>
            <v-tooltip location="top" open-delay="500">
              <template v-slot:activator="{ props }">
                <span v-bind="props">{{ entityCardType?.title || 'Aggregate Root' }}</span>
              </template>
              <span>Aggregate Root and associated entities</span>
            </v-tooltip>
          </th>
          <th>
            <v-tooltip location="top" open-delay="500">
              <template v-slot:activator="{ props }">
                <span v-bind="props" style="white-space: nowrap;">Resulting event</span>
              </template>
              <span>The result of the role invoking the command on the aggregate</span>
            </v-tooltip>
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(system, systemIndex) in boundedContext.systems" :key="systemIndex">
          <domain-model-system-row
            v-for="event in system.events"
            :key="event.id"
            :system="system"
            :event="event"
            :boundedContext="boundedContext"
            :showEntities="showEntities"
            :showReadModels="showReadModels"
            :refreshKey="refreshKey"
            @refreshArrows="refreshArrows"
          />
        </template>
      </tbody>
    </table>
    <!-- #svg defs -->
    <svg width="0" height="0">
      <defs>
        <marker
          id="arrowhead-horizontal"
          markerWidth="10"
          markerHeight="10"
          refX="0"
          refY="5"
          orient="0"
        >
          <polygon points="0 0, 10 5, 0 10" fill="rgb(118, 118, 118)" />
        </marker>
      </defs>
      <defs>
        <marker
          id="arrowhead-auto"
          markerWidth="10"
          markerHeight="10"
          refX="10"
          refY="5"
          orient="auto"
        >
          <polygon points="0 0, 10 5, 0 10" fill="rgb(118, 118, 118)" />
        </marker>
      </defs>
      <defs>
        <marker
          id="arrowhead-vertical"
          markerWidth="10"
          markerHeight="10"
          refX="10"
          refY="5"
          orient="auto"
        >
          <polygon points="0 0, 10 5, 0 10" fill="rgb(118, 118, 118)" />
        </marker>
      </defs>
    </svg>
    <!-- #end svg defs -->
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import DomainModelSystemRow from '@/components/DomainModelSystemRow.vue'

const {
  boundedContext,
  showEntities,
  showReadModels,
  refreshTrigger
} = defineProps({
  boundedContext: Object,
  showEntities: Boolean,
  showReadModels: Boolean,
  refreshTrigger: Number
})

const refreshKey = ref(0)

const store = useStore()
const requirementTypes = computed(() => store.state.requirementTypes.requirementTypes)

const queryCardType = computed(() => diagramHelpers.methods.getQueryCardType(requirementTypes.value))
const entityCardType = computed(() => diagramHelpers.methods.getAggregateCardType(requirementTypes.value))
const commandCardType = computed(() => diagramHelpers.methods.getCommandCardType(requirementTypes.value))

const refreshArrows = async () => {
  await nextTick()
  refreshKey.value++
}

const onContainerScroll = () => {
  refreshArrows()
}

watch([() => refreshTrigger, () => boundedContext], () => {
  refreshArrows()
})
</script>

<style scoped>
tr {
  th {
    padding: 8px 28px;
    font-size: 14px;
    font-weight: 400;

    &:last-child {
      text-align: right;
    }
  }
}

:deep(.requirement-card) {
  min-width: 200px;
  max-width: 300px;
  margin: 0 auto;
  overflow: hidden;
  padding: 12px;
  border-radius: 8px;
  background: #FFF;
  border: 1px solid black;
  border-top-width: 2px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.15);
}

:deep(.add-card-btn) {
  border: 2px solid var(--border-color);
}

:deep(.field-name) {
  font-size: 13px;
}

:deep(.cursor-handle) {
  cursor: grab;
}

:deep(.sortable-chosen) {
  background: #eee;
}

:deep(.sortable-ghost) {
  background: #E5E3E1;

  & > div {
    opacity: 0.01;
  }
}

:deep(.btn-delete) {
  opacity: 0;
}

:deep(.sortableRow) {

  &:hover .btn-delete {
    opacity: 0.7;

    &:hover {
      opacity: 1;
    }
  }
}

:deep(.sortableSubRow) {

  &:hover .btn-delete {
    opacity: 1;
  }
}

:deep(.card-menu) {
  .v-list-item:hover {
    background-color: var(--light-2) !important;
  }
}

:deep(.model-container) {
  position: relative;
  width: 100%;
  z-index: 1;
}

:deep(.related-entity-chip) {
  position: relative;

  &&& .v-chip__close {
    margin-inline-start: 0;
    margin-inline-end: 0;
    position: absolute;
    right: 0;
    background: #e8e8e8;
    opacity: 0;
    transition: all 0.2s;
  }

  &:hover {
    &&& .v-chip__close {
      opacity: 1;
    }
  }
}

:deep(.sortHandle) {
  margin-left: -4px;
}

:deep(.integration-divider) {
  font-size: 12px;
  width: 100%;
  background-color: gray;
  text-align: center;
}
</style>
